import { FiBookmark, FiSearch, FiEye, FiShare2, FiArrowLeft, FiX, FiMoreHorizontal, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaFacebook, FaFacebookMessenger, FaXTwitter, FaWhatsapp, FaTelegram } from "react-icons/fa6";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./usercss/learn.css";

import testingImg from "../../images/testing.png";
import { publishedService, userService, bookmarkService, statsService } from "../../services/firestoreService";
import { auth, db } from "../../firebase";
import { collection, onSnapshot, getDoc, doc } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";

// Age verification utility function - optimized to use cached data


// Helper function to calculate age from DOB
const calculateAgeFromDob = (dob) => {
    // Parse DOB - handle both string and timestamp formats
    let birthDate;
    if (dob.toDate) {
        birthDate = dob.toDate();
    } else if (typeof dob === 'string') {
        birthDate = new Date(dob);
    } else if (dob instanceof Date) {
        birthDate = dob;
    } else {
        return false;
    }
    
    if (isNaN(birthDate.getTime())) {
        return false;
    }
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    
    return age < 18;
};


export default function Learn() {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const t = translations[language];
    const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
    const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showBookmarkPanel, setShowBookmarkPanel] = useState(false);
    const [shareModal, setShareModal] = useState({ isOpen: false, article: null });
    // Read localStorage cache synchronously during first render so the skeleton
    // is never shown when warm cache exists — eliminates the blank screen on refresh.
    const getInitialCachedCards = () => {
        try {
            const stored = localStorage.getItem('auravit_published_cache');
            if (stored) {
                const { data, timestamp } = JSON.parse(stored);
                const age = Date.now() - timestamp;
                if (age < 30 * 60 * 1000 && Array.isArray(data) && data.length > 0) return data;
            }
        } catch (_) {}
        return [];
    };

    const [copyButtonText, setCopyButtonText] = useState("Copy Link");
    const [backendCards, setBackendCards] = useState(() => getInitialCachedCards());
    const [selectedTopics, setSelectedTopics] = useState(new Set());
    const [suggestedArticles, setSuggestedArticles] = useState([]);
    const [articleStats, setArticleStats] = useState({});
    const [userIsMinor, setUserIsMinor] = useState(false);
    // Start as not-loading if we already have cached cards to show
    const [isLoading, setIsLoading] = useState(() => getInitialCachedCards().length === 0);
    const [contentLoaded, setContentLoaded] = useState(() => getInitialCachedCards().length > 0);
    const [currentPage, setCurrentPage] = useState(1);
    const [bookmarkCurrentPage, setBookmarkCurrentPage] = useState(1);
    const CARDS_PER_PAGE = 8;
    
    const bookmarkInitRef = useRef(false);
    const currentUserIdRef = useRef(null);
    const bookmarkListenerRef = useRef(null);

    // Stable userId string — avoids re-running effects when auth.currentUser object reference changes
    const [userId, setUserId] = useState(() => auth.currentUser?.uid ?? null);
    useEffect(() => {
        return auth.onAuthStateChanged(user => setUserId(user?.uid ?? null));
    }, []);
    const contentPreferencesListenerRef = useRef(null);
    const userDataCacheRef = useRef(null);
    const lastContentSnapshotRef = useRef(null);
    const userIsMinorRef = useRef(false);
    const rawPublishedRef = useRef([]);
    const publishedListenerRef = useRef(null);
    const storageEventHandlerRef = useRef(null);

    // Reset to page 1 whenever topic filters, minor status, or search change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTopics, userIsMinor]);

    useEffect(() => {
        if (!userId) {
            setBookmarkedIds(new Set());
            currentUserIdRef.current = null;
            bookmarkInitRef.current = false;
            return;
        }

        if (bookmarkInitRef.current && currentUserIdRef.current === userId) {
            return;
        }

        currentUserIdRef.current = userId;
        bookmarkInitRef.current = true;

        // Only sync pending local operations to Firestore — no getDocs read needed here.
        // The onSnapshot listener below is the single source of truth for bookmark state.
        bookmarkService.syncPendingOperations?.(userId)?.catch(() => {});
    }, [userId]);

    // Set up real-time listener for bookmark subcollection updates - optimized to prevent re-subscriptions
    useEffect(() => {
        const user = auth.currentUser;
        const userId = user?.uid;
        
        if (!userId) {
            // Cleanup existing listener if user logs out
            if (bookmarkListenerRef.current) {
                bookmarkListenerRef.current();
                bookmarkListenerRef.current = null;
            }
            return;
        }
        
        // Skip if listener is already set up for this user
        if (bookmarkListenerRef.current && currentUserIdRef.current === userId) {
            return;
        }
        
        // Cleanup previous listener if user changed
        if (bookmarkListenerRef.current) {
            bookmarkListenerRef.current();
            bookmarkListenerRef.current = null;
        }
        
        let mounted = true;
        
        const bookmarksRef = collection(db, 'users', userId, 'bookmarks');
        const unsubscribe = onSnapshot(bookmarksRef, (querySnapshot) => {
            if (!mounted) return;
            
            const bookmarksData = {};
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                bookmarksData[doc.id] = data.bookmarked || true;
            });
            
            // Convert to Set for UI compatibility
            const bookmarkedItemIds = new Set(
                Object.keys(bookmarksData).filter(itemId => bookmarksData[itemId])
            );
            setBookmarkedIds(bookmarkedItemIds);
        }, (error) => {
            // Error in real-time bookmark listener
        });

        // Store unsubscribe function
        bookmarkListenerRef.current = unsubscribe;

        // Cleanup listener on unmount or user change
        return () => {
            mounted = false;
            if (bookmarkListenerRef.current) {
                bookmarkListenerRef.current();
                bookmarkListenerRef.current = null;
            }
        };
    }, [userId]); // Re-setup when user changes

    // Fetch user content preferences from Firestore - optimized with real-time listener
    useEffect(() => {
        const user = auth.currentUser;
        const userId = user?.uid;
        
        if (!userId) {
            setSelectedTopics(new Set());
            userDataCacheRef.current = null;
            // Cleanup existing listener if user logs out
            if (contentPreferencesListenerRef.current) {
                contentPreferencesListenerRef.current();
                contentPreferencesListenerRef.current = null;
            }
            return;
        }
        
        // Skip if listener is already set up for this user
        if (contentPreferencesListenerRef.current && currentUserIdRef.current === userId) {
            return;
        }
        
        // Cleanup previous listener if user changed
        if (contentPreferencesListenerRef.current) {
            contentPreferencesListenerRef.current();
            contentPreferencesListenerRef.current = null;
        }
        
        let mounted = true;
        
        // Set up real-time listener on user document for content preferences - OPTIMIZED
        const userRef = doc(db, 'users', userId);
        const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
            if (!mounted) return;
            
            if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                // Cache user data for age check optimization
                userDataCacheRef.current = userData;

                // Set userIsMinor directly from cached data to avoid duplicate getDoc
                if (userData) {
                    const dob = userData.dateOfBirth || userData.personalInformation?.dob || userData.birthDate || userData.dob;
                    const assistance = userData.assistance || {};
                    const reasons = assistance.reasonForAssistance || [];
                    const isMinorByAssistance = Array.isArray(reasons) ? reasons.includes("minor") : reasons === "minor";
                    
                    if (dob) {
                        const isMinor = calculateAgeFromDob(dob);
                        setUserIsMinor(isMinor || isMinorByAssistance);
                    } else {
                        setUserIsMinor(isMinorByAssistance);
                    }
                }

                if (userData && userData.contentPreferences) {
                    const preferences = userData.contentPreferences;
                    const selectedTopicIds = preferences.selectedTopics || [];
                    const topics = preferences.topics || [];

                    // Check if "Community Outreach Programs" is in topics array (matching UserProfile title)
                    const hasCommunityOutreach = topics.includes("Community Outreach Programs") || topics.includes("Community Outreach Program");

                    // Add or remove topic ID 9 (Community Outreach Program) based on topics array
                    const newTopics = new Set(selectedTopicIds);
                    if (hasCommunityOutreach) {
                        newTopics.add(9);
                    } else {
                        newTopics.delete(9);
                    }

                    setSelectedTopics(newTopics);
                }
            }
        }, (error) => {
            // Error in content preferences listener
        });

        // Also listen for storage events from UserProfile for cross-tab communication
        const handleStorageChange = (e) => {
            if (e.key === 'contentPreferencesUpdated') {
                // Force refresh content preferences when storage event is triggered
                const userData = userDataCacheRef.current;
                if (userData && userData.contentPreferences) {
                    const preferences = userData.contentPreferences;
                    const selectedTopicIds = preferences.selectedTopics || [];
                    const topics = preferences.topics || [];

                    // Check if "Community Outreach Programs" is in topics array (matching UserProfile title)
                    const hasCommunityOutreach = topics.includes("Community Outreach Programs") || topics.includes("Community Outreach Program");

                    // Add or remove topic ID 9 (Community Outreach Program) based on topics array
                    const newTopics = new Set(selectedTopicIds);
                    if (hasCommunityOutreach) {
                        newTopics.add(9);
                    } else {
                        newTopics.delete(9);
                    }

                    setSelectedTopics(newTopics);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        storageEventHandlerRef.current = handleStorageChange;

        // Store unsubscribe function
        contentPreferencesListenerRef.current = unsubscribe;

        // Cleanup listener on unmount or user change
        return () => {
            mounted = false;
            if (contentPreferencesListenerRef.current) {
                contentPreferencesListenerRef.current();
                contentPreferencesListenerRef.current = null;
            }
            if (storageEventHandlerRef.current) {
                window.removeEventListener('storage', storageEventHandlerRef.current);
                storageEventHandlerRef.current = null;
            }
        };
    }, [userId, userDataCacheRef]);

    useEffect(() => {
        userIsMinorRef.current = userIsMinor;
        const raw = rawPublishedRef.current;
        if (!raw || raw.length === 0) return;
        const isMinor = userIsMinor;
        const mapped = raw.map((content) => {
            const readTime = content.readTime || 1;
            return {
                id: content.id,
                title: content.title || content.socialTitle || "Untitled",
                description: content.socialDescription || content.subtitle || "",
                img: content.socialImageUrl || testingImg,
                category: content.category || "General Health",
                createdAt: content.publishedAt ? new Date(content.publishedAt).getTime() : (content.approvedAt ? new Date(content.approvedAt).getTime() : 0),
                _isBackend: true,
                contentId: content.contentId,
                stats: content.stats || { views: 0, shares: 0, bookmarks: 0 },
                readTime,
                minorEligible: content.minorEligible
            };
        });
        const filtered = isMinor
            ? mapped.filter(c => c.minorEligible === true || c.minorEligible === 'true')
            : mapped;
        setBackendCards(filtered);
        const statsMap = {};
        filtered.forEach(a => {
            const cid = a.contentId || a.id;
            statsMap[cid] = a.stats || { views: 0, shares: 0, bookmarks: 0 };
        });
        setArticleStats(statsMap);
    }, [userIsMinor]);

    useEffect(() => {
        let active = true;
        // Only show the skeleton if we have no cached cards to display.
        // When cache is warm, keep showing the existing cards while the
        // background fetch silently updates them.
        const hasCachedCards = backendCards.length > 0;
        if (!hasCachedCards) {
            setIsLoading(true);
            setContentLoaded(false);
        }
        (async () => {
            try {
                // userIsMinor is now set by the onSnapshot listener, no need for duplicate check
                if (publishedListenerRef.current) {
                    publishedListenerRef.current();
                    publishedListenerRef.current = null;
                }
                
                // Function to fetch published content (one-time, not real-time)
                const fetchPublishedContent = async () => {
                    try {
                        const publishedContent = await publishedService.getAllPublished();
                        const currentSnapshot = JSON.stringify(publishedContent.map(c => ({
                            id: c.id,
                            v: c.stats?.views ?? 0,
                            s: c.stats?.shares ?? 0,
                            b: c.stats?.bookmarks ?? 0,
                            m: c.minorEligible
                        })));
                        if (lastContentSnapshotRef.current === currentSnapshot) {
                            if (active) {
                                setIsLoading(false);
                                setContentLoaded(true);
                            }
                            return;
                        }
                        lastContentSnapshotRef.current = currentSnapshot;
                        rawPublishedRef.current = publishedContent;
                        const isMinor = userIsMinorRef.current;
                        const mapped = publishedContent.map((content) => {
                            // Use stored readTime — it is calculated and saved when content is published
                            const readTime = content.readTime || 1;
                            return {
                                id: content.id,
                                title: content.title || content.socialTitle || "Untitled",
                                description: content.socialDescription || content.subtitle || "",
                                img: content.socialImageUrl || testingImg,
                                category: content.category || "General Health",
                                createdAt: content.publishedAt ? new Date(content.publishedAt).getTime() : (content.approvedAt ? new Date(content.approvedAt).getTime() : 0),
                                _isBackend: true,
                                contentId: content.contentId,
                                stats: content.stats || { views: 0, shares: 0, bookmarks: 0 },
                                // bodyHtml is NOT stored on card objects — it is large and only
                                // needed by the article detail view, which fetches the full doc itself
                                readTime,
                                minorEligible: content.minorEligible
                            };
                        });
                        const filtered = isMinor
                            ? mapped.filter(c => c.minorEligible === true || c.minorEligible === 'true')
                            : mapped;
                        setBackendCards(filtered);
                        const statsMap = {};
                        filtered.forEach(a => {
                            const cid = a.contentId || a.id;
                            statsMap[cid] = a.stats || { views: 0, shares: 0, bookmarks: 0 };
                        });
                        setArticleStats(statsMap);
                        if (active) {
                            setIsLoading(false);
                            setContentLoaded(true);
                        }
                    } catch (error) {
                        // Error fetching published content
                        if (active) {
                            setIsLoading(false);
                            setContentLoaded(true);
                        }
                    }
                };

                // Initial fetch — awaited so loading state only clears after data is set.
                await fetchPublishedContent();

                // Set up periodic background refresh every 5 minutes
                const refreshInterval = setInterval(() => {
                    if (active) {
                        fetchPublishedContent();
                    }
                }, 300000);

                publishedListenerRef.current = () => {
                    clearInterval(refreshInterval);
                };
            } catch (e) {
                // Error setting up published content fetch — keep skeleton showing
            }
        })();

        return () => {
            active = false;
            if (publishedListenerRef.current) {
                publishedListenerRef.current();
                publishedListenerRef.current = null;
            }
        };
    }, [userId]);

    // Generate suggested articles based on filtering criteria (only on initial load)
    useEffect(() => {
        const generateSuggestedArticles = () => {
            const user = auth.currentUser;
            if (!user || backendCards.length === 0) {
                setSuggestedArticles([]);
                return;
            }

            // Filter out bookmarked articles
            const nonBookmarkedArticles = backendCards.filter(article => 
                !bookmarkedIds.has(article.contentId || article.id)
            );
            
            // Filter by user's content preferences (strict - only show if preferences are selected)
            if (selectedTopics.size === 0) {
                setSuggestedArticles([]);
                return;
            }

            const categoryToTopicId = {
                'Menstrual Wellness and Hygiene': 1,
                'Contraception': 2,
                'Fertility Basics': 3,
                'STI Awareness': 4,
                'Mental Health in Reproductive Care': 5,
                'Male Reproductive Health': 6,
                'Pregnancy Prevention': 7,
                'General Reproductive Wellness': 8,
                'Community Outreach Programs': 9
            };

            const preferenceFilteredArticles = nonBookmarkedArticles.filter(article => {
                const topicId = categoryToTopicId[article.category];
                const matchesPreferences = topicId && selectedTopics.has(topicId);
                
                // Additional check: only show content eligible for minors if user is minor
                if (userIsMinor) {
                    if (article.minorEligible !== true && article.minorEligible !== 'true') {
                        return false;
                    }
                }
                
                return matchesPreferences;
            });
            
            // Randomly select 2 articles from eligible content
            if (preferenceFilteredArticles.length >= 2) {
                const shuffled = [...preferenceFilteredArticles].sort(() => Math.random() - 0.5);
                const selected = shuffled.slice(0, 2);
                setSuggestedArticles(selected);
            } else {
                setSuggestedArticles([]);
            }
        };

        // Generate suggestions when component mounts, data is available, or user minor status changes
        if (backendCards.length > 0) {
            generateSuggestedArticles();
        }
    }, [backendCards.length, userIsMinor]); // Re-run when backendCards count changes or userIsMinor changes

    const lookupPublishedDoc = (id) => {
        const raw = rawPublishedRef.current || [];
        return raw.find(c => (c.contentId || c.id) === id) || raw.find(c => c.id === id) || null;
    };

    const handleArticleClick = useCallback(async (article) => {
        const articleId = article.contentId || article.id;
        const user = auth.currentUser;

        try {
            if (user) {
                await statsService.incrementViews(articleId, user.uid, {
                    cachedUserData: userDataCacheRef.current,
                    cachedPublishedDoc: lookupPublishedDoc(articleId)
                });
                // Invalidate cache so navigating back shows fresh view count
                publishedService.invalidateAllPublishedCache();
            }
        } catch (error) {
            // Error incrementing views
        }

        navigate(`/learn/article/${articleId}`);
    }, [navigate]);

    const toggleBookmark = useCallback(async (e, id, title) => {
        e.stopPropagation();

        const user = auth.currentUser;
        if (!user) return;

        try {
            const isCurrentlyBookmarked = bookmarkedIds.has(id);
            const newBookmarkState = await bookmarkService.toggleBookmark(user.uid, id, title);
            const opts = {
                cachedUserData: userDataCacheRef.current,
                cachedPublishedDoc: lookupPublishedDoc(id)
            };
            if (newBookmarkState) {
                await statsService.incrementBookmarks(id, user.uid, opts);
                // Invalidate cache so navigating back shows fresh counts
                publishedService.invalidateAllPublishedCache();
                // Update local stats immediately
                setArticleStats(prev => ({
                    ...prev,
                    [id]: {
                        ...prev[id],
                        bookmarks: (prev[id]?.bookmarks || 0) + 1
                    }
                }));
            } else {
                await statsService.decrementBookmarks(id, user.uid, opts);
                // Invalidate cache so navigating back shows fresh counts
                publishedService.invalidateAllPublishedCache();
                // Update local stats immediately
                setArticleStats(prev => ({
                    ...prev,
                    [id]: {
                        ...prev[id],
                        bookmarks: Math.max(0, (prev[id]?.bookmarks || 0) - 1)
                    }
                }));
            }
            setBookmarkedIds((prev) => {
                const next = new Set(prev);
                if (newBookmarkState) next.add(id);
                else next.delete(id);
                return next;
            });
        } catch (error) {
            // Error toggling bookmark
        }
    }, [bookmarkedIds]);

    const allCards = backendCards;

    // Create mapping from category names to topic IDs
    const categoryToTopicId = {
        'Menstrual Wellness and Hygiene': 1,
        'Contraception': 2,
        'Fertility Basics': 3,
        'STI Awareness': 4,
        'Mental Health in Reproductive Care': 5,
        'Male Reproductive Health': 6,
        'Pregnancy Prevention': 7,
        'General Reproductive Wellness': 8,
        'Community Outreach Programs': 9 
    };

    // Filter cards based on user's content preferences and minor eligibility
    // Memoized — only recalculates when cards, preferences, or minor status actually change
    const preferenceFilteredCards = useMemo(() => selectedTopics.size > 0
        ? allCards.filter((card) => {
            const topicId = categoryToTopicId[card.category];
            const matchesPreferences = topicId ? selectedTopics.has(topicId) : true;
            if (userIsMinor) {
                if (card.minorEligible !== true && card.minorEligible !== 'true') return false;
            }
            return matchesPreferences;
        })
        : allCards.filter((card) => {
            if (userIsMinor) {
                if (card.minorEligible !== true && card.minorEligible !== 'true') return false;
            }
            return true;
        }),
    [allCards, selectedTopics, userIsMinor]);

    const sortNewestFirst = (a, b) => {
        const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (ad && bd) return bd - ad;
        // fallback for static numeric IDs
        const an = typeof a.id === "number" ? a.id : parseInt(a.id, 10);
        const bn = typeof b.id === "number" ? b.id : parseInt(b.id, 10);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an;
        return String(b.id).localeCompare(String(a.id));
    };

    // Filter cards based on user's content preferences (for main feed only)
    // Memoized — only recalculates when preferenceFilteredCards, bookmarks, or show state changes
    const filteredCards = useMemo(() =>
        showBookmarkedOnly
            ? preferenceFilteredCards.filter((c) => bookmarkedIds.has(c.id))
            : preferenceFilteredCards,
    [preferenceFilteredCards, bookmarkedIds, showBookmarkedOnly]);
    
    // Bookmarks should bypass content preferences but still respect minor eligibility if user is minor
    // Memoized — only recalculates when allCards, bookmarks, or minor status changes
    const bookmarkedFilteredCards = useMemo(() => allCards.filter((card) => {
        const isBookmarked = bookmarkedIds.has(card.id);
        if (!isBookmarked) return false;
        if (userIsMinor) {
            if (card.minorEligible !== true && card.minorEligible !== 'true') return false;
        }
        return true;
    }), [allCards, bookmarkedIds, userIsMinor]);

    // Pagination for bookmarks
    const bookmarkTotalPages = Math.ceil(bookmarkedFilteredCards.length / CARDS_PER_PAGE);
    const bookmarkStartIndex = (bookmarkCurrentPage - 1) * CARDS_PER_PAGE;
    const bookmarkEndIndex = bookmarkCurrentPage * CARDS_PER_PAGE;
    const bookmarkedCardsToShow = bookmarkedFilteredCards.slice(
        bookmarkStartIndex,
        bookmarkEndIndex
    );
    
    const searchLower = searchQuery.trim().toLowerCase();
    const allFilteredCards = searchLower
        ? filteredCards.filter(
            (c) =>
                c.title.toLowerCase().includes(searchLower) ||
                c.description.toLowerCase().includes(searchLower)
        )
        : filteredCards;

    const totalPages = Math.ceil(allFilteredCards.length / CARDS_PER_PAGE);
    const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
    const endIndex = currentPage * CARDS_PER_PAGE;
    const cardsToShow = allFilteredCards.slice(
        startIndex,
        endIndex
    );

    const handleSeeAllClick = () => {
        setShowBookmarkPanel(true);
    };

    const handleBackClick = () => {
        setShowBookmarkPanel(false);
        setBookmarkCurrentPage(1);
        // Reset scroll to top
        const workspaceElement = document.querySelector('.learn-workspace');
        if (workspaceElement) {
            workspaceElement.scrollTop = 0;
        }
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    const handleShareClick = useCallback(async (e, article) => {
        e.stopPropagation();
        try {
            const articleId = article.contentId || article.id;
            const user = auth.currentUser;
            if (user) {
                await statsService.incrementShares(articleId, user.uid, {
                    cachedUserData: userDataCacheRef.current,
                    cachedPublishedDoc: lookupPublishedDoc(articleId)
                });
                // Invalidate cache so navigating back shows fresh counts
                publishedService.invalidateAllPublishedCache();
                // Update local stats immediately
                setArticleStats(prev => ({
                    ...prev,
                    [articleId]: {
                        ...prev[articleId],
                        shares: (prev[articleId]?.shares || 0) + 1
                    }
                }));
            }
        } catch (error) {
            // Error incrementing shares
        }
        setShareModal({ isOpen: true, article });
        setCopyButtonText("Copy Link");
    }, []);

    const handleCloseShareModal = useCallback(() => {
        setShareModal({ isOpen: false, article: null });
        setCopyButtonText("Copy Link");
    }, []);

    const handleCopyLink = useCallback(async () => {
        if (shareModal.article) {
            const articleId = shareModal.article.contentId || shareModal.article.id;
            const url = `https://auravit-cfsi.com/learn/article/${articleId}`;
            try {
                await navigator.clipboard.writeText(url);
                setCopyButtonText("Copied!");
                setTimeout(() => setCopyButtonText("Copy Link"), 2000);
            } catch (err) {
                // Failed to copy link
            }
        }
    }, [shareModal.article]);
    
    const handleSocialShare = useCallback((platform) => {
        if (!shareModal.article) return;
        const articleId = shareModal.article.contentId || shareModal.article.id;
        const articleUrl = `https://auravit-cfsi.com/learn/article/${articleId}`;
        const url = encodeURIComponent(articleUrl);
        const title = encodeURIComponent(shareModal.article.title || "");
        const description = encodeURIComponent(shareModal.article.description || "");

        let shareUrl = "";

        switch (platform) {
            case "facebook":
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                break;
            case "messenger":
                const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
                if (facebookAppId) {
                    const redirectUri = encodeURIComponent(window.location.origin);
                    shareUrl = `https://www.facebook.com/dialog/send?link=${url}&app_id=${facebookAppId}&redirect_uri=${redirectUri}&display=popup`;
                    window.open(shareUrl, "_blank", "width=600,height=500,noopener,noreferrer");
                    return;
                } else if (navigator.share) {
                    navigator.share({
                        title: shareModal.article.title || "",
                        text: shareModal.article.description || "",
                        url: articleUrl
                    }).catch(err => {
                        fallbackShare(articleUrl, shareModal.article.title);
                    });
                    return;
                } else {
                    fallbackShare(articleUrl, shareModal.article.title);
                    return;
                }
            case "twitter":
                shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
                break;
            case "whatsapp":
                shareUrl = `https://api.whatsapp.com/send?text=${title}%20${url}`;
                break;
            case "telegram":
                shareUrl = `https://t.me/share/url?url=${url}&text=${title}`;
                break;
            default:
                return;
        }

        if (shareUrl) {
            window.open(shareUrl, "_blank", "noopener,noreferrer");
        }
    }, [shareModal.article]);
    
    const fallbackShare = (articleUrl, title) => {
        const copyLink = window.confirm("Messenger share not available. Would you like to copy the article link instead?");
        if (copyLink) {
            handleCopyLink();
        }
    };

    // Helper function to get article stats
    const getArticleStats = (article) => {
        const articleId = article.contentId || article.id;
        const stats = articleStats[articleId] || article.stats || { views: 0, shares: 0, bookmarks: 0 };
        return stats;
    };

    // Helper function to calculate read time based on word count
    const calculateReadTime = (content) => {
        if (!content || typeof content !== 'string') {
            return 1; // Default to 1 minute for empty/invalid content
        }

        // Strip HTML tags if content contains HTML
        const textContent = content.replace(/<[^>]+>/g, '');
        
        // Trim and split by whitespace to count words
        const words = textContent.trim().split(/\s+/);
        const wordCount = words.length;
        
        // Calculate read time using 200 words per minute, always round up
        const readTime = Math.ceil(wordCount / 200);
        
        // Minimum 1 minute
        return Math.max(1, readTime);
    };

    // Helper function to get read time for an article
    const getArticleReadTime = (article) => {
        // Use stored read time if available, otherwise calculate from bodyHtml
        if (article.readTime) {
            return article.readTime;
        }
        
        // Try to calculate from bodyHtml if available
        if (article.bodyHtml) {
            return calculateReadTime(article.bodyHtml);
        }
        
        // Fallback to 1 minute for articles without content
        return 1;
    };

    return (
        <section className="learn-container">
            {/* Header */}
            <div className="learn-header">
                <h1>{t.educationalHub}</h1>
                <div className="learn-search-container">
                    <div className="learn-search-box">
                        <div className="learn-search-box-inner">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder={t.search}
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Skeleton Loading State */}
            {isLoading && !contentLoaded && (
                <div className="learn-content">
                    <div className="learn-workspace">
                        <div className="learn-article-list">
                            {[...Array(6)].map((_, index) => (
                                <div className="article-item skeleton-article-item" key={index}>
                                    <div className="article-content">
                                        <div className="article-header">
                                            <div className="skeleton skeleton-category-small"></div>
                                            <div className="skeleton skeleton-date-small"></div>
                                        </div>
                                        <div className="skeleton skeleton-title-medium"></div>
                                        <div className="skeleton skeleton-description"></div>
                                        <div className="article-footer">
                                            <div className="skeleton skeleton-read-time"></div>
                                            <div className="article-stats">
                                                <div className="skeleton skeleton-stat"></div>
                                                <div className="skeleton skeleton-stat"></div>
                                                <div className="skeleton skeleton-stat"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="article-right">
                                        <div className="skeleton skeleton-date-top"></div>
                                        <div className="skeleton skeleton-image-card"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="learn-up-next">
                        <div className="up-next-section">
                            <div className="skeleton skeleton-up-next-header"></div>
                            <div className="up-next-list">
                                {[...Array(3)].map((_, index) => (
                                    <div className="up-next-item skeleton-up-next-item" key={index}>
                                        <div className="up-next-content">
                                            <div className="up-next-header-info">
                                                <div className="skeleton skeleton-category-small"></div>
                                                <div className="skeleton skeleton-bookmark-icon"></div>
                                            </div>
                                            <div className="up-next-title-row">
                                                <div className="skeleton skeleton-title-small"></div>
                                                <div className="skeleton skeleton-image-small"></div>
                                            </div>
                                            <div className="up-next-footer">
                                                <div className="skeleton skeleton-read-time-small"></div>
                                                <div className="up-next-stats">
                                                    <div className="skeleton skeleton-stat-small"></div>
                                                    <div className="skeleton skeleton-stat-small"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            {!isLoading && (showBookmarkPanel ? (
                <div className="learn-content bookmark-panel">
                    {/* Main workspace - scrollable */}
                    <div className="learn-workspace">
                        {/* Sticky header with back button and pagination */}
                        <div className="bookmark-panel-header">
                            <button className="back-button" onClick={handleBackClick}>
                                <FiArrowLeft />
                            </button>
                            {bookmarkTotalPages > 1 && (
                                <div className="pagination-container">
                                    <div className="pagination-info">
                                        <span>Showing {bookmarkStartIndex + 1}-{Math.min(bookmarkEndIndex, bookmarkedFilteredCards.length)} of {bookmarkedFilteredCards.length} records</span>
                                    </div>
                                    <div className="pagination-controls">
                                        <button
                                            className="pagination-btn pagination-btn--prev"
                                            onClick={() => setBookmarkCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={bookmarkCurrentPage === 1}
                                        >
                                            <FiChevronLeft />
                                            Previous
                                        </button>

                                        <div className="pagination-numbers">
                                            {Array.from({ length: bookmarkTotalPages }, (_, index) => index + 1).map((pageNumber) => (
                                                <button
                                                    key={pageNumber}
                                                    className={`pagination-number ${bookmarkCurrentPage === pageNumber ? 'pagination-number--active' : ''}`}
                                                    onClick={() => setBookmarkCurrentPage(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </button>
                                            ))}
                                        </div>

                                        <button
                                            className="pagination-btn pagination-btn--next"
                                            onClick={() => setBookmarkCurrentPage(p => Math.min(bookmarkTotalPages, p + 1))}
                                            disabled={bookmarkCurrentPage === bookmarkTotalPages || bookmarkTotalPages === 0}
                                        >
                                            Next
                                            <FiChevronRight />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Article list */}
                        <div className="learn-article-list">
                            {bookmarkedCardsToShow
                                .length > 0 ? (
                                [...bookmarkedCardsToShow]
                                    .sort(sortNewestFirst) // Latest first
                                    .map((card) => (
                                        <div className="article-item" key={card.id}>
                                            <div className="article-content">
                                                <div className="article-header">
                                                    <span className="article-category">{card.category.toUpperCase()}</span>
                                                    <span className="article-date">MAR 2</span>
                                                </div>
                                                <h3 
                                                    className="article-title clickable-title" 
                                                    onClick={() => handleArticleClick(card)}
                                                >
                                                    {card.title}
                                                </h3>
                                                <p className="article-description">{card.description}</p>
                                                <div className="article-footer">
                                                    <span className="read-time">{getArticleReadTime(card)} MIN READ</span>
                                                    <div className="article-stats">
                                                        <div className="stat-item">
                                                            <FiEye className="stat-icon" />
                                                            <span className="stat-number">{getArticleStats(card).views}</span>
                                                        </div>
                                                        <div className="stat-item">
                                                            <FiShare2 
                                                                className="stat-icon"
                                                                onClick={(e) => handleShareClick(e, card)}
                                                            />
                                                            <span className="stat-number">{getArticleStats(card).shares}</span>
                                                        </div>
                                                        <div className="stat-item">
                                                            <FiBookmark 
                                                                className={`stat-icon bookmark-icon ${bookmarkedIds.has(card.id) ? "bookmarked" : ""}`}
                                                                onClick={(e) => toggleBookmark(e, card.id, card.title)}
                                                            />
                                                            <span className="stat-number">{getArticleStats(card).bookmarks}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="article-right">
                                                <div className="article-date-top">MAR 2</div>
                                                <div className="article-image">
                                                    <img src={card.img} alt={card.title} loading="lazy" />
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            ) : (
                                <div className="no-bookmarked-content">
                                    <p>{t.noRecordsFound}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="learn-content">
                    {/* Main workspace - scrollable */}
                    <div className="learn-workspace">
                        {/* Article list */}
                        <div className="learn-article-list">
                            {cardsToShow.map((card) => (
                                <div className="article-item" key={card.id}>
                                    <div className="article-content">
                                        <div className="article-header">
                                            <span className="article-category">{card.category.toUpperCase()}</span>
                                            <span className="article-date">MAR 2</span>
                                        </div>
                                        <h3 
                                                    className="article-title clickable-title" 
                                                    onClick={() => handleArticleClick(card)}
                                                >
                                                    {card.title}
                                                </h3>
                                        <p className="article-description">{card.description}</p>
                                        <div className="article-footer">
                                            <span className="read-time">{getArticleReadTime(card)} MIN READ</span>
                                            <div className="article-stats">
                                                <div className="stat-item">
                                                    <FiEye className="stat-icon" />
                                                    <span className="stat-number">{getArticleStats(card).views}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <FiShare2 
                                                        className="stat-icon"
                                                        onClick={(e) => handleShareClick(e, card)}
                                                    />
                                                    <span className="stat-number">{getArticleStats(card).shares}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <FiBookmark 
                                                        className={`stat-icon bookmark-icon ${bookmarkedIds.has(card.id) ? "bookmarked" : ""}`}
                                                        onClick={(e) => toggleBookmark(e, card.id, card.title)}
                                                    />
                                                    <span className="stat-number">{getArticleStats(card).bookmarks}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="article-right">
                                        <div className="article-date-top">MAR 2</div>
                                        <div className="article-image">
                                            <img src={card.img} alt={card.title} loading="lazy" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination controls */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination-info">
                                    <span>Showing {startIndex + 1}-{Math.min(endIndex, allCards.length)} of {allCards.length} articles</span>
                                </div>
                                <div className="pagination-controls">
                                    <button 
                                        className="pagination-btn pagination-btn--prev" 
                                        onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); }}
                                        disabled={currentPage === 1}
                                    >
                                        <FiChevronLeft />
                                        Previous
                                    </button>
                                    
                                    <div className="pagination-numbers">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                                            <button
                                                key={pageNumber}
                                                className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                                                onClick={() => setCurrentPage(pageNumber)}
                                                style={currentPage === pageNumber ? {
                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                    border: '1px solid rgba(59, 130, 246, 0.4)',
                                                    color: '#ffffff',
                                                    fontWeight: '700',
                                                    boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4), 0 6px 16px rgba(37, 99, 235, 0.3)',
                                                    transform: 'translateY(-2px)'
                                                } : {}}
                                            >
                                                {pageNumber}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <button 
                                        className="pagination-btn pagination-btn--next" 
                                        onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); }}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                        <FiChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}

                                            </div>

                    {/* Right side panel */}
                    <div className="learn-up-next">
                        {/* Up Next Section - only show if there are bookmarks */}
                        {allCards.filter((card) => bookmarkedIds.has(card.id)).length > 0 && (
                            <div className="up-next-section">
                                <div className="up-next-header-row">
                                    <h3 className="up-next-header">{t.upNext}</h3>
                                    <span className="up-next-see-all" onClick={handleSeeAllClick}>See Bookmarks</span>
                                </div>
                                <div className="up-next-list">
                                    {[...bookmarkedFilteredCards]
                                        .sort(sortNewestFirst) // Latest first
                                        .slice(0, 3)
                                        .map((card) => (
                                            <div className="up-next-item" key={card.id}>
                                                <div className="up-next-content">
                                                    <div className="up-next-header-info">
                                                        <span className="up-next-category">{card.category.toUpperCase()}</span>
                                                        <FiBookmark 
                                                            className={`up-next-bookmark-icon ${bookmarkedIds.has(card.id) ? "bookmarked" : ""}`}
                                                            onClick={(e) => toggleBookmark(e, card.id, card.title)}
                                                        />
                                                    </div>
                                                    <div className="up-next-title-row">
                                                        <h4 
                                                                className="up-next-title clickable-title"
                                                                onClick={() => handleArticleClick(card)}
                                                            >
                                                            {card.title}
                                                        </h4>
                                                        <div className="up-next-image">
                                                            <img src={card.img} alt={card.title} loading="lazy" />
                                                        </div>
                                                    </div>
                                                    <div className="up-next-footer">
                                                        <span className="up-next-read-time">{getArticleReadTime(card)} MIN READ</span>
                                                        <div className="up-next-stats">
                                                            <div className="up-next-stat-item">
                                                                <FiEye className="up-next-stat-icon" />
                                                                <span className="up-next-stat-number">{getArticleStats(card).views}</span>
                                                            </div>
                                                            <div className="up-next-stat-item">
                                                                <FiShare2 className="up-next-stat-icon" />
                                                                <span className="up-next-stat-number">{getArticleStats(card).shares}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Suggested Articles Section - always show */}
                        <div className="up-next-section">
                            <div className="up-next-header-row">
                                <h3 className="up-next-header">{t.suggestedArticles}</h3>
                            </div>
                            <div className="up-next-list">
                                {suggestedArticles.map((article) => (
                                    <div className="up-next-item" key={article.id}>
                                        <div className="up-next-content">
                                            <div className="up-next-header-info">
                                                <span className="up-next-category">{article.category.toUpperCase()}</span>
                                            </div>
                                            <div className="up-next-title-row">
                                                <h4 
                                                    className="up-next-title clickable-title"
                                                    onClick={() => handleArticleClick(article)}
                                                >
                                                    {article.title}
                                                </h4>
                                                <div className="up-next-image">
                                                    <img src={article.img} alt={article.title} loading="lazy" />
                                                </div>
                                            </div>
                                            <div className="up-next-footer">
                                                <span className="up-next-read-time">{getArticleReadTime(article)} MIN READ</span>                                                <div className="up-next-stats">
                                                    <div className="up-next-stat-item">
                                                        <FiEye className="up-next-stat-icon" />
                                                        <span className="up-next-stat-number">{getArticleStats(article).views}</span>
                                                    </div>
                                                    <div className="up-next-stat-item">
                                                        <FiShare2 className="up-next-stat-icon" />
                                                        <span className="up-next-stat-number">{getArticleStats(article).shares}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {/* Share Modal */}
            {shareModal.isOpen && (
                <div className="share-modal-overlay" onClick={handleCloseShareModal}>
                    <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <h3 className="share-modal-title">Share Article</h3>
                            <button className="share-modal-close" onClick={handleCloseShareModal}>
                                <FiX />
                            </button>
                        </div>
                        <div className="share-modal-content">
                            <div className="share-content-preview">
                                <h4 className="share-preview-title">{shareModal.article?.title}</h4>
                                <p className="share-preview-description">{shareModal.article?.description}</p>
                            </div>
                            <div className="share-social-section">
                                <div className="share-social-buttons">
                                    <button 
                                        className="share-social-button share-facebook"
                                        onClick={() => handleSocialShare("facebook")}
                                        title="Share on Facebook"
                                    >
                                        <FaFacebook />
                                    </button>
                                    <button 
                                        className="share-social-button share-twitter"
                                        onClick={() => handleSocialShare("twitter")}
                                        title="Share on X/Twitter"
                                    >
                                        <FaXTwitter />
                                    </button>
                                    <button 
                                        className="share-social-button share-whatsapp"
                                        onClick={() => handleSocialShare("whatsapp")}
                                        title="Share on WhatsApp"
                                    >
                                        <FaWhatsapp />
                                    </button>
                                    <button 
                                        className="share-social-button share-telegram"
                                        onClick={() => handleSocialShare("telegram")}
                                        title="Share on Telegram"
                                    >
                                        <FaTelegram />
                                    </button>
                                    <button 
                                        className="share-social-button share-messenger"
                                        onClick={() => handleSocialShare("messenger")}
                                        title="More Options"
                                    >
                                        <FiMoreHorizontal />
                                    </button>
                                </div>
                            </div>
                            <div className="share-url-section">
                                <label className="share-url-label">Article URL:</label>
                                <div className="share-url-box">
                                    <input
                                        type="text"
                                        className="share-url-input"
                                        value={`https://auravit-cfsi.com/learn/article/${shareModal.article?.contentId || shareModal.article?.id}`}
                                        readOnly
                                    />
                                    <button 
                                        className={`share-copy-button ${copyButtonText === "Copied!" ? "copied" : ""}`}
                                        onClick={handleCopyLink}
                                    >
                                        {copyButtonText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </section>
    );
}
